import ArrowBack from "@mui/icons-material/ArrowBack";
import { Button, ButtonBase, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { type FC, useEffect } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { notEmpty } from "../../../utils/not-empty";
import { ChannelIcon } from "../../channels/components/channel-icon";
import { prettyChannelName } from "../../channels/utils";
import type { MomentFormMoment } from "../../moment/types/moment-form-moment";

type SurveyAlertWizardStep2Values = Pick<MomentFormMoment, "channel">;

type SurveyAlertWizardStep2Props = {
  channels: MomentFormMoment["composeMethods"];
  onCompleteChange: (complete: boolean) => void;
  onBack: () => void;
};

export const SurveyAlertWizardStep2: FC<SurveyAlertWizardStep2Props> = ({
  channels,
  onBack,
  onCompleteChange,
}) => {
  const { control } = useFormContext<SurveyAlertWizardStep2Values>();
  const selectedChannel = useWatch({ control, name: "channel" });

  useEffect(() => {
    onCompleteChange(Boolean(selectedChannel));
  }, [onCompleteChange, selectedChannel]);

  return (
    <>
      <DialogTitle>
        <Stack spacing={1} alignItems="flex-start">
          <Button
            startIcon={<ArrowBack />}
            onClick={onBack}
            data-analytics-id="survey-alert-wizard-back"
          >
            Back
          </Button>
          <Typography variant="h6" component="span">
            Notification Channel
          </Typography>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={4}>
          <Typography variant="body2">
            Decide how you’d like to send the response notification.
          </Typography>
          <Controller
            name="channel"
            control={control}
            render={({ field }) => (
              <Stack spacing={2}>
                {channels
                  .filter(notEmpty)
                  .filter((channel) => channel !== "twilio" && channel !== "whatsapp")
                  .map((channel) => {
                    const selected = field.value === channel;

                    return (
                      <ButtonBase
                        key={channel}
                        onClick={() => field.onChange(channel)}
                        aria-pressed={selected}
                        data-analytics-id={`survey-alert-wizard-channel-${channel}`}
                        sx={{
                          width: "100%",
                          height: 56,
                          justifyContent: "flex-start",
                          gap: 2,
                          px: 3,
                          border: 1,
                          borderWidth: selected ? 3 : 1,
                          borderColor: selected ? "primary.main" : "divider",
                          borderRadius: 1,
                        }}
                      >
                        <ChannelIcon channel={channel} sx={{ width: 40, height: 40 }} />
                        <Typography variant="body2">{prettyChannelName(channel)}</Typography>
                      </ButtonBase>
                    );
                  })}
              </Stack>
            )}
          />
        </Stack>
      </DialogContent>
    </>
  );
};
